import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories, movies } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function MovieCategories() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredMovies: Movie[] = activeCategory === 'all'
    ? movies.slice(0, 8)
    : movies.filter((m) => m.category === activeCategory).slice(0, 8);

  const activeCat = categories.find((c) => c.key === activeCategory);

  return (
    <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
            电影分类
          </h2>
          <p className="text-sm text-foreground-500 mt-2">
            按类型浏览，快速找到心仪影片
          </p>
        </div>

        <div className="flex items-center justify-center flex-wrap gap-2 mb-10">
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeCategory === cat.key
                  ? 'bg-primary-500 text-background-50 shadow-sm'
                  : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200'
              }`}
            >
              <i className={`${cat.icon} text-sm`}></i>
              {cat.name}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="true">
          {filteredMovies.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>

        {activeCat && (
          <div className="text-center mt-8">
            <Link
              to={`/category/${activeCat.key}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 hover:bg-background-200 border border-background-300 text-foreground-700 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部{activeCat.name}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

function MovieCard({ movie }: { movie: Movie }) {
  return (
    <Link
      to={`/article/${movie.id}`}
      className="group block bg-background-50 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={movie.poster}
          alt={`${movie.title} - 小草影院${movie.subCategory}`}
          title={`${movie.title} - ${movie.subCategory}在线观看`}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-2 left-2">
          <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
            {movie.subCategory}
          </span>
        </div>
        {movie.isNew && (
          <div className="absolute top-2 right-2">
            <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
              NEW
            </span>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-background-950/80 to-transparent">
          <div className="flex items-center gap-1">
            <i className="ri-star-fill text-accent-400 text-xs"></i>
            <span className="text-xs text-background-100 font-medium">{movie.rating}</span>
          </div>
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-medium text-foreground-900 truncate font-heading">
          {movie.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1 flex items-center gap-2">
          <span>{movie.year}</span>
          <span className="text-foreground-300">|</span>
          <span>{movie.duration}</span>
        </p>
      </div>
    </Link>
  );
}